/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import type { FeatureCollection, GeoJsonProperties, Polygon } from 'geojson';
import { atom } from 'jotai';
import type { PrimitiveAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useState } from 'react';

import { NIFC_BOUNDS_GEOMETRY_LADDER, useGeometryDetail, type Bbox } from '../map/geometryDetail';
import type { GeometryVariant } from '../services/featuresApi';
import { fetchFlatGeobuf } from '../services/featuresApi';

const TABLE_MAP = {
  perimeter: 'nifc_bounds',
  post: 'nifc_post',
} as const;

// The base perimeter set is national and drawn at dashboard zooms, where the
// 0.0005 deg simplified column is sub-pixel (it costs 118 kB against 2,331 kB
// full, measured on local data) -- the one whole-table rung of
// NIFC_BOUNDS_GEOMETRY_LADDER. Zooming to where that generalization would show
// swaps in full-resolution geometry for the viewport -- see
// useNifcPerimeterDetail.
//
// nifc_post is absent: its USNG cells are axis-aligned squares with nothing to
// generalize, so the server offers no variant for that table.
const TABLE_GEOMETRY: Partial<Record<keyof typeof TABLE_MAP, GeometryVariant>> = {
  perimeter: NIFC_BOUNDS_GEOMETRY_LADDER.whole[0].variant,
};

// Columns the app actually reads, so the response carries no dead properties.
// For nifc_post that is priority_rank (the cell colour), every population column
// (each selectable as the legend attribute and the extrusion height) and the popup
// keys, plus incidentname, which the "Color by" percentile colouring ranks the
// cells within (POST_EVENT_OF); incidentcreatedate is dropped, since the
// perimeters carry it. nifc_bounds is deliberately unprojected: every one of
// its non-geometry columns (incidentname, createdate, run_time,
// structures_exposed) is read, and attributes are ~0.1% of its payload.
const TABLE_FIELDS: Partial<Record<keyof typeof TABLE_MAP, string[]>> = {
  post: [
    'usng1km',
    'incidentname',
    'run_time',
    'state',
    'county',
    'fips',
    'ls_day_pop',
    'ls_night_pop',
    'pop',
    'households',
    'housingunits',
    'pop65over',
    'pop16overunemployed',
    'pop16overnotinlaborforce',
    'popamindianalaskan',
    'householdsinpoverty',
    'householdsdsfs',
    'householdsdsnofs',
    'householdswithfs',
    'householdswithpa',
    'housingunitsmobilehomes',
    'lep',
    'quantile_sum',
    'final_rank',
    'priority_rank',
  ],
};

export async function asyncGetNifcData<P extends GeoJsonProperties = GeoJsonProperties>(
  type: 'perimeter' | 'post'
): Promise<FeatureCollection<Polygon, P>> {
  const table = TABLE_MAP[type];
  return fetchFlatGeobuf<Polygon, P>(table, {
    fields: TABLE_FIELDS[type],
    geometry: TABLE_GEOMETRY[type],
  });
}

export type NifcPerimeterDetail<P extends GeoJsonProperties = GeoJsonProperties> = {
  /**
   * The viewport the collection was fetched over, carried alongside it so a
   * consumer merging the two resolutions knows which perimeters this set
   * accounts for -- including while a newer viewport's fetch is still in flight
   * and this one is what is drawn.
   */
  bbox: Bbox;
  collection: FeatureCollection<Polygon, P>;
};

/**
 * Full-resolution perimeters for the current viewport, or null while the
 * simplified base set is faithful enough on its own.
 *
 * Where the 0.0005 deg generalization would show, the user must see the real
 * perimeter, but the whole national set at full resolution is the 2,331 kB against
 * 118 kB measured above -- and that on 64 local perimeters, where a fire season
 * carries hundreds -- so only what is on screen is fetched. A superseded request is
 * aborted, cancelling the server's query too, and the previous detail keeps
 * rendering until its replacement resolves.
 */
export function useNifcPerimeterDetail<P extends GeoJsonProperties = GeoJsonProperties>(
  enabled: boolean
): NifcPerimeterDetail<P> | null {
  const { detail: request } = useGeometryDetail(NIFC_BOUNDS_GEOMETRY_LADDER);
  const [detail, setDetail] = useState<NifcPerimeterDetail<P> | null>(null);

  useEffect(() => {
    if (!enabled || request == null) return;

    const { variant, bbox } = request;
    const controller = new AbortController();
    fetchFlatGeobuf<Polygon, P>(TABLE_MAP.perimeter, {
      bbox,
      geometry: variant,
      fields: TABLE_FIELDS.perimeter,
      signal: controller.signal,
    })
      .then(collection => {
        if (!controller.signal.aborted) setDetail({ bbox, collection });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // The simplified base set stays on the map, so a failure costs fidelity
        // rather than the perimeters themselves.
        console.error('Error fetching full-resolution NIFC perimeters:', error);
      });

    return () => {
      controller.abort();
    };
  }, [enabled, request]);

  // Disabled, or no viewport to refine: the last response is declined here
  // rather than cleared from the effect, which would cost a second render pass.
  return !enabled || request == null ? null : detail;
}

const _nifcDataChunksAtom = atomFamily((_param: string) =>
  atom<FeatureCollection<Polygon, GeoJsonProperties>[]>([])
);

export function nifcDataChunksAtom<T>(
  id: 'post' | 'perimeter'
): PrimitiveAtom<FeatureCollection<Polygon, T>[]> {
  return _nifcDataChunksAtom(id) as unknown as PrimitiveAtom<FeatureCollection<Polygon, T>[]>;
}

export function useSetNifcDataChunks<T extends GeoJsonProperties>(
  type: 'perimeter' | 'post',
  dataChunks: FeatureCollection<Polygon, T>[]
): () => Promise<void> {
  return useAtomCallback(
    useCallback(
      async (_get, set) => {
        if (dataChunks.length > 0) return;
        const data = await asyncGetNifcData<T>(type);
        set(nifcDataChunksAtom<T>(type), [data]);
      },
      [dataChunks.length, type]
    )
  );
}
